import {
  LabelledUserWithSitesAndProcesses,
  userModelToLabelledWithSitesAndProcesses,
} from 'shared/domain/user/mapping/toView';
import type { UserModel } from 'shared/domain/user/types/model';
import { UserRole } from 'shared/types/userRole';
import { filterOutSuperAdmin } from './filterSuperAdmin';
import { isAdmin } from 'shared/domain/role/isAdmin';

/**
 * Checks if user has access to given site and process.
 * Admin roles (superAdmin, organizationAdmin, projectAdmin) have implicit access
 * to ALL sites and processes - their stored arrays are empty by design.
 */
function hasAccessToSiteAndProcess(
  user: UserModel,
  site: string,
  process: string
): boolean {
  return isAdmin(user.role) || (user.sites.includes(site) && user.processes.includes(process));
}

export function usersWithoutSuperAdminSelector(
  users: UserModel[],
  organizationId: string
): LabelledUserWithSitesAndProcesses[] {
  return users
    .filter(filterOutSuperAdmin)
    .map((user) => userModelToLabelledWithSitesAndProcesses(user, organizationId));
}

/**
 * Returns users who have access to the given site and process.
 *
 * Note: Admin roles (superAdmin, organizationAdmin, projectAdmin) are returned
 * regardless of site/process parameters because they have implicit access to
 * everything. Their sites/processes arrays are empty by design, not because
 * they lack access.
 */
export function usersWithAccessSelector(
  roles: UserRole[],
  site: string,
  process: string,
  users: UserModel[],
  organizationId: string
): LabelledUserWithSitesAndProcesses[] {
  return users
    .filter((user) => roles.includes(user.role) && hasAccessToSiteAndProcess(user, site, process))
    .map((user) => userModelToLabelledWithSitesAndProcesses(user, organizationId));
}
